import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Baseball, Basketball, SignOut, User, ShieldStar, CaretDown, ChartLineUp, Lightning, House } from "@phosphor-icons/react";

const navItem = ({ isActive }) =>
  `relative px-3 py-2 text-xs uppercase tracking-[0.2em] transition-all ${
    isActive
      ? "text-white font-bold bg-white/[0.06] after:absolute after:left-2 after:right-2 after:bottom-0 after:h-[2px] after:bg-[#22C55E] after:content-['']"
      : "text-zinc-400 font-semibold hover:text-white hover:bg-white/[0.03]"
  }`;

const PUBLIC_LINKS = [
  { to: "/sports/mlb", label: "Picks", end: true, testid: "nav-picks" },
  { to: "/receipts", label: "Receipts", testid: "nav-receipts" },
  { to: "/history", label: "History", testid: "nav-history" },
  { to: "/weekly", label: "Weekly", testid: "nav-weekly" },
];

const ADMIN_LINKS = [
  { to: "/sports/mlb", label: "Picks", end: true, testid: "nav-picks" },
  { to: "/scoreboard", label: "Scoreboard", testid: "nav-scoreboard" },
  { to: "/standings", label: "Standings", testid: "nav-standings" },
  { to: "/streaks", label: "Streak Lab", testid: "nav-streaks" },
  { to: "/betting", label: "Signal Lab", testid: "nav-betting" },
  { to: "/strategy", label: "Strategy", testid: "nav-strategy" },
  { to: "/big-road", label: "Big Road", testid: "nav-bigroad" },
  { to: "/lab", label: "Test Lab", testid: "nav-lab" },
  { to: "/admin/subscriptions", label: "Subs", testid: "nav-admin-subs" },
  { to: "/admin/broadcast", label: "Email", testid: "nav-admin-broadcast" },
];

const PRODUCTS = [
  { to: "/sports/mlb", label: "MLB Predictions", color: "#007AFF", icon: Baseball, sub: "Live" },
  { to: "/sports/wnba", label: "WNBA Predictions", color: "#FF6B35", icon: Basketball, sub: "Included" },
  { to: "/markets/stocks", label: "Stock Predictions", color: "#F59E0B", icon: ChartLineUp, sub: "Live" },
  { to: "/markets/options", label: "Option Predictions", color: "#A855F7", icon: Lightning, sub: "Live" },
  { to: "/pricing", label: "Pricing", color: "#22C55E", icon: ShieldStar, sub: "Plans + bundles" },
];

function ProductSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const current =
    PRODUCTS.find((p) => location.pathname.startsWith(p.to))
    || (location.pathname.startsWith("/sports/mlb") ? PRODUCTS[0] : null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (!current) return null; // hide on root portal page

  const CurIcon = current.icon;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-product-switcher"
        className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-white/40 rounded-sm text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors"
      >
        <CurIcon size={13} weight="fill" style={{ color: current.color }} />
        <span>{current.label}</span>
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-[#121212] border border-white/15 rounded-sm shadow-2xl overflow-hidden z-50" data-testid="product-switcher-menu">
          <Link to="/" className="flex items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:bg-white/5 border-b border-white/10" data-testid="switcher-portal-home">
            <House size={12} /> All Products
          </Link>
          {PRODUCTS.map((p) => {
            const PIcon = p.icon;
            const isCur = current.to === p.to;
            return (
              <Link
                key={p.to}
                to={p.to}
                data-testid={`switcher-${p.to.split("/").pop()}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-b-0"
              >
                <PIcon size={16} weight="fill" style={{ color: p.color }} />
                <div className="flex-1">
                  <div className={`text-sm ${isCur ? "text-white font-bold" : "text-zinc-300"}`}>{p.label}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: p.color }}>{p.sub}</div>
                </div>
                {isCur && <div className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!(user && typeof user === "object" && user.role === "admin");
  const location = useLocation();
  // Hide MLB sub-nav when on the portal landing or teaser pages — they have
  // their own navigation cues.
  const onMlbContext =
    location.pathname.startsWith("/sports/mlb") ||
    location.pathname === "/picks" ||
    ["/receipts", "/history", "/weekly", "/scoreboard", "/standings", "/streaks",
     "/betting", "/strategy", "/big-road", "/lab"].some((p) => location.pathname.startsWith(p));
  const links = isAdmin ? ADMIN_LINKS : PUBLIC_LINKS;

  return (
    <header className="sticky top-0 z-40 bg-black/95 backdrop-blur border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <Baseball size={28} weight="fill" className="text-[#007AFF]" />
            <div className="leading-none">
              <div className="font-display text-2xl tracking-wider">ACCURATE<span className="text-[#007AFF]"> </span>MONKEY</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-0.5">PICKS · MARKETS · OPTIONS</div>
            </div>
          </Link>
          <ProductSwitcher />
        </div>

        {onMlbContext && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={navItem} data-testid={l.testid}>
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2">
          {isAdmin && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#007AFF] border border-[#007AFF]/40 px-2 py-0.5 rounded-sm" data-testid="admin-badge">
              <ShieldStar size={12} weight="fill" /> Admin
            </span>
          )}
          {user && typeof user === "object" ? (
            <>
              <Link
                to="/account"
                data-testid="nav-account-link"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-[#007AFF]/60 hover:text-white rounded-sm transition-colors"
              >
                <User size={14} className="text-zinc-400" />
                <span className="text-xs font-mono-d text-zinc-300" data-testid="nav-user-email">{user.email}</span>
              </Link>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-white/10 hover:border-[#FF3B30]/50 hover:text-[#FF3B30] text-zinc-300 rounded-sm transition-colors"
              >
                <SignOut size={14} /> Sign out
              </button>
            </>
          ) : user === false ? (
            <>
              <Link to="/getting-started" data-testid="nav-gs-link" className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-white/15 hover:border-white/40 text-zinc-300 rounded-sm font-bold transition-colors">
                How it works
              </Link>
              <Link to="/optimizer" data-testid="nav-optimizer-link" className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-white/15 hover:border-white/40 text-zinc-300 rounded-sm font-bold transition-colors">
                Optimizer
              </Link>
              <Link to="/pricing" data-testid="nav-pricing-link" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#22C55E]/40 hover:border-[#22C55E] hover:bg-[#22C55E]/10 text-[#22C55E] rounded-sm font-bold transition-colors">
                <ShieldStar size={13} weight="fill" /> Pricing
              </Link>
              <Link to="/pricing" data-testid="nav-signup-link" className="hidden sm:inline-block px-4 py-1.5 text-xs uppercase tracking-wider bg-[#22C55E] hover:bg-[#16A34A] text-black rounded-sm font-bold transition-colors">
                Subscribe
              </Link>
              <Link to="/login" data-testid="nav-login-link" className="px-4 py-1.5 text-xs uppercase tracking-wider border border-white/20 hover:border-white/50 text-zinc-300 rounded-sm font-bold transition-colors">
                Sign In
              </Link>
            </>
          ) : user && user.role !== "admin" ? (
            <>
              <Link to="/optimizer" data-testid="nav-optimizer-link" className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-white/15 hover:border-white/40 text-zinc-300 rounded-sm font-bold transition-colors">
                Optimizer
              </Link>
              <Link to="/pricing" data-testid="nav-upgrade-link" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#22C55E]/40 hover:border-[#22C55E] hover:bg-[#22C55E]/10 text-[#22C55E] rounded-sm font-bold transition-colors">
                <ShieldStar size={13} weight="fill" /> Plans
              </Link>
            </>
          ) : null}
        </div>
      </div>
      <div className="md:hidden border-t border-white/5 flex items-center justify-around overflow-x-auto">
        {onMlbContext && links.map((l) => (
          <NavLink key={l.to + "-m"} to={l.to} end={l.end} className={navItem} data-testid={l.testid + "-m"}>
            {l.label}
          </NavLink>
        ))}
      </div>
    </header>
  );
}
